// RelatorioTabular.js - Relatório em estilo planilha (Interface Web)
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Grid, Chip, Autocomplete, Alert, CircularProgress,
  IconButton, Tooltip, TablePagination
} from '@mui/material';
import {
  Refresh, FilterList, CalendarToday,
  TrendingUp, TrendingDown, Remove, Print
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const RelatorioTabular = () => {
  const { api } = useAuth();
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dados, setDados] = useState([]);
  const [dadosProcessados, setDadosProcessados] = useState([]);
  const [totaisDiarios, setTotaisDiarios] = useState({});
  const [totaisDiariosLeituras, setTotaisDiariosLeituras] = useState({});
  const [totaisDiariosVolume, setTotaisDiariosVolume] = useState({});
  const [metrica, setMetrica] = useState('distinct'); // 'distinct' | 'leituras' | 'volume' | 'volume_formatado'
  
  // Estados de filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    horaInicio: '00:00',
    horaFim: '23:59',
    setor: '',
    funcionario: ''
  });
  const [opcoesFiltro, setOpcoesFiltro] = useState({
    setores: [],
    funcionarios: []
  });
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  
  // Estados de visualização
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [diasDoMes, setDiasDoMes] = useState([]);
  const [resultadoValidacao, setResultadoValidacao] = useState(null);
  const [validando, setValidando] = useState(false);

  // Carregar opções de filtro - função simples sem dependências
  const carregarOpcoesFiltro = async () => {
    try {
      const [resSetores, resFuncionarios] = await Promise.all([
        api.listarSetores(),
        api.listarFuncionarios()
      ]);

      setOpcoesFiltro({
        setores: resSetores.success ? resSetores.dados : [],
        funcionarios: resFuncionarios.success ? resFuncionarios.dados : []
      });
    } catch (error) {
      // Erro silencioso
    }
  };

  // Gerar dias do período (timezone-safe) sempre em ordem crescente
  const gerarDiasDoPeriodo = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return [];
    const dias = [];
    const inicio = new Date(`${dataInicio}T00:00:00`);
    const fim = new Date(`${dataFim}T23:59:59`);
    const cursor = new Date(inicio.getTime());
    const nomesSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
    while (cursor <= fim) {
      const ano = cursor.getFullYear();
      const mes = String(cursor.getMonth() + 1).padStart(2, '0');
      const diaNum = String(cursor.getDate()).padStart(2, '0');
      const dataStr = `${ano}-${mes}-${diaNum}`;
      dias.push({
        data: dataStr,
        dia: cursor.getDate(),
        diaSemana: nomesSemana[cursor.getDay()]
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return dias;
  };

  // Carregar dados do relatório
  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Limpar dados anteriores para evitar duplicação
      setDados([]);
      setDadosProcessados([]);
      setTotaisDiarios({});
      setTotaisDiariosLeituras({});
      setTotaisDiariosVolume({});
      setPage(0);

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };

      if (filtros.setor) parametros.setor = filtros.setor;
      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;

      const res = await api.relatorioTabular(parametros);

      if (res.success) {
        const dadosTabular = res.dados;

        // Extrair estruturas vindas do backend
  const funcionariosApi = dadosTabular.funcionarios || [];
  const totaisPorDiaDistinctApi = dadosTabular.totais_por_dia || {};
  const totaisPorDiaLeiturasApi = dadosTabular.totais_por_dia_leituras || {};
  const totaisPorDiaVolumeApi = dadosTabular.totais_por_dia_volume || {};

        // Gerar dias confiáveis a partir do filtro (evita dia 31 do mês anterior, etc.)
        const diasGerados = gerarDiasDoPeriodo(filtros.dataInicio, filtros.dataFim);
        const diasValidosSet = new Set(diasGerados.map(d => d.data));

        // Normalizar funcionários e filtrar produção apenas para dias válidos
        const funcionariosFormatados = funcionariosApi.map(func => {
          const prodDistinct = func.producao_diaria || {};
          const prodLeituras = func.producao_diaria_leituras || {};
          const prodVolume = func.producao_diaria_volume || {};
          const filtradoDistinct = Object.fromEntries(Object.entries(prodDistinct).filter(([data]) => diasValidosSet.has(data)));
          const filtradoLeituras = Object.fromEntries(Object.entries(prodLeituras).filter(([data]) => diasValidosSet.has(data)));
          const filtradoVolume = Object.fromEntries(Object.entries(prodVolume).filter(([data]) => diasValidosSet.has(data)));
          const totalDistinct = Object.values(filtradoDistinct).reduce((a,b)=>a+(b||0),0);
          const totalLeituras = Object.values(filtradoLeituras).reduce((a,b)=>a+(b||0),0);
          const totalVolume = Object.values(filtradoVolume).reduce((a,b)=>a+(b||0),0);
          return {
            codigo: func.codigo,
            nome: func.nome,
            setor: func.setor,
            producaoDiariaDistinct: filtradoDistinct,
            producaoDiariaLeituras: filtradoLeituras,
            producaoDiariaVolume: filtradoVolume,
            producaoDiariaVolumeFormatado: func.producao_diaria_volume_formatado || {},
            totalDistinct,
            totalLeituras,
            totalVolume,
            tiposVolume: func.tipos_volume || []
          };
        });

        // Filtrar apenas funcionários reais (excluir linha SOMA TOTAL) para cálculo dos totais
        const funcionariosReais = funcionariosFormatados.filter(f => f.codigo !== 'TOTAL');

        // Recalcular totais por dia apenas para dias válidos (mantém zeros onde não há produção)
        // Usar apenas funcionários reais para não duplicar valores com a linha SOMA TOTAL
        const totaisDistinctFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosReais.reduce((soma, f) => soma + (f.producaoDiariaDistinct[d.data] || 0), 0);
          return acc;
        }, {});
        const totaisLeiturasFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosReais.reduce((soma, f) => soma + (f.producaoDiariaLeituras[d.data] || 0), 0);
          return acc;
        }, {});
        const totaisVolumeFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosReais.reduce((soma, f) => soma + (f.producaoDiariaVolume[d.data] || 0), 0);
          return acc;
        }, {});

        // Atualizar estados
        setDados(funcionariosFormatados);
        setDiasDoMes(diasGerados);
  setDadosProcessados(funcionariosFormatados);
  setTotaisDiarios(totaisDistinctFiltrados);
  setTotaisDiariosLeituras(totaisLeiturasFiltrados);
  setTotaisDiariosVolume(totaisVolumeFiltrados);
      } else {
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // FUNÇÃO REMOVIDA - Os dados já vêm processados do backend
  // processarDadosProdutividade não é mais necessária

  // Processar dados para formato tabular (método antigo - manter para compatibilidade)
  const processarDados = (dadosOriginais) => {
    const funcionarios = {};
    const totaisPorDia = {};

    // Inicializar totais diários
    diasDoMes.forEach(dia => {
      totaisPorDia[dia.data] = 0;
    });

    // Agrupar por funcionário e dia
    dadosOriginais.forEach(item => {
      const dataItem = item.data.split('T')[0]; // YYYY-MM-DD
      const funcionarioCodigo = item.codigo_funcionario;
      const funcionarioNome = item.funcionario_nome || `Funcionário ${funcionarioCodigo}`;
      const setor = item.setor_efetivo || item.setor_leitura || item.funcionario_setor || 'Sem Setor';

      if (!funcionarios[funcionarioCodigo]) {
        funcionarios[funcionarioCodigo] = {
          codigo: funcionarioCodigo,
          nome: funcionarioNome,
          setor: setor,
          producaoDiaria: {},
          total: 0
        };
        
        // Inicializar todos os dias com 0
        diasDoMes.forEach(dia => {
          funcionarios[funcionarioCodigo].producaoDiaria[dia.data] = new Set();
        });
      }

      // Adicionar requisição única por dia
      if (funcionarios[funcionarioCodigo].producaoDiaria[dataItem]) {
        funcionarios[funcionarioCodigo].producaoDiaria[dataItem].add(item.codigo_requisicao);
      }
    });

    // Converter Sets para números e calcular totais
    const dadosFinais = Object.values(funcionarios).map(func => {
      let totalFuncionario = 0;
      const producaoFinal = {};

      Object.keys(func.producaoDiaria).forEach(data => {
        const quantidade = func.producaoDiaria[data].size;
        producaoFinal[data] = quantidade;
        totalFuncionario += quantidade;
        totaisPorDia[data] += quantidade;
      });

      return {
        ...func,
        producaoDiaria: producaoFinal,
        total: totalFuncionario
      };
    });

    // Ordenar por total decrescente
    dadosFinais.sort((a, b) => b.total - a.total);

    setDadosProcessados(dadosFinais);
    setTotaisDiarios(totaisPorDia);
  };

  // Definir período padrão (mês atual) - APENAS UMA VEZ
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    // Gerar dias do mês atual usando função centralizada
    const diasArray = gerarDiasDoPeriodo(
      primeiroDia.toISOString().split('T')[0],
      ultimoDia.toISOString().split('T')[0]
    );
    setDiasDoMes(diasArray);

    // Carregar opções de filtro apenas uma vez
    carregarOpcoesFiltro();
  }, []); // Sem dependências para executar apenas uma vez

  // Remover o useEffect que reprocessa dados - não é mais necessário
  // pois os dados já vêm processados do backend

  // Aplicar cor baseada na produção
  const obterCorCelula = (valor) => {
    if (valor === 0) return '#ffebee'; // Vermelho claro
    if (valor < 50) return '#fff3e0'; // Laranja claro
    if (valor < 100) return '#f3e5f5'; // Roxo claro
    if (valor < 150) return '#e8f5e8'; // Verde claro
    return '#e3f2fd'; // Azul claro (alta produção)
  };

  // Aplicar cor do texto baseada na produção
  const obterCorTexto = (valor) => {
    if (valor === 0) return '#c62828';
    if (valor < 50) return '#ef6c00';
    if (valor < 100) return '#7b1fa2';
    if (valor < 150) return '#2e7d32';
    return '#1565c0';
  };

  // Renderizar célula de produção
  const renderCelulaProducao = (valor, data) => {
    const isWeekend = new Date(data).getDay() === 0 || new Date(data).getDay() === 6;

    // Verificar se é string (volume formatado) ou número
    const isVolumeFormatado = typeof valor === 'string' && valor.trim() !== '';
    const isNumero = typeof valor === 'number' || (!isNaN(parseFloat(valor)) && !isVolumeFormatado);
    const valorNumerico = isNumero ? parseFloat(valor) : 0;
    const temValor = isVolumeFormatado || valorNumerico > 0;

    return (
      <TableCell
        key={data}
        align="center"
        sx={{
          backgroundColor: isVolumeFormatado ? '#e8f5e8' : obterCorCelula(valorNumerico),
          color: isVolumeFormatado ? '#2e7d32' : obterCorTexto(valorNumerico),
          fontWeight: temValor ? 'bold' : 'normal',
          border: '1px solid #e0e0e0',
          minWidth: '80px',
          fontSize: '0.875rem',
          opacity: isWeekend ? 0.7 : 1,
          position: 'relative'
        }}
      >
        {temValor ? valor : '-'}
        {isWeekend && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 6,
              height: 6,
              backgroundColor: '#757575',
              borderRadius: '50%'
            }}
          />
        )}
      </TableCell>
    );
  };

  const dadosPaginados = dadosProcessados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Verificação de segurança - garantir que todos os dados têm a estrutura correta
  const dadosSegurosPaginados = dadosPaginados.map(funcionario => ({
    ...funcionario,
    producaoDiariaDistinct: funcionario.producaoDiariaDistinct || {},
    producaoDiariaLeituras: funcionario.producaoDiariaLeituras || {},
    producaoDiariaVolume: funcionario.producaoDiariaVolume || {},
    producaoDiariaVolumeFormatado: funcionario.producaoDiariaVolumeFormatado || {},
    totalDistinct: funcionario.totalDistinct || 0,
    totalLeituras: funcionario.totalLeituras || 0,
    totalVolume: funcionario.totalVolume || 0,
    tiposVolume: funcionario.tiposVolume || []
  }));

  const executarValidacao = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período para validar');
      return;
    }
    try {
      setValidando(true);
      setResultadoValidacao(null);
      // Mesma regra do carregamento principal: enviar somente datas (sem horário)
      const parametros = { 
        dataInicio: filtros.dataInicio, 
        dataFim: filtros.dataFim 
      };
      if (filtros.setor) parametros.setor = filtros.setor;
      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;
      const res = await api.relatorioTabularValidar(parametros);
      setResultadoValidacao(res);
    } catch (e) {
      console.error('Erro na validação', e);
    } finally {
      setValidando(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3,
      '@media print': {
        '& .no-print': { display: 'none !important' },
        '& .MuiTableContainer-root': { maxHeight: 'none !important' },
        '& .MuiTable-root': { fontSize: '10px !important' },
        '& .MuiTableCell-root': { padding: '2px !important' }
      }
    }}>

      <Typography variant="h4" gutterBottom>
        Relatório Tabular de Produção
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Hora Início"
              type="time"
              value={filtros.horaInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Hora Fim"
              type="time"
              value={filtros.horaFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Setor</InputLabel>
              <Select
                value={filtros.setor}
                onChange={(e) => setFiltros(prev => ({ ...prev, setor: e.target.value }))}
                label="Setor"
              >
                <MenuItem value="">Todos</MenuItem>
                {opcoesFiltro.setores.map(setor => (
                  <MenuItem key={setor.id} value={setor.nome}>
                    {setor.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={opcoesFiltro.funcionarios}
              getOptionLabel={(option) => option.nome}
              value={funcionarioSelecionado}
              onChange={(event, newValue) => setFuncionarioSelecionado(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Funcionário" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Métrica</InputLabel>
              <Select
                value={metrica}
                label="Métrica"
                onChange={(e) => setMetrica(e.target.value)}
              >
                <MenuItem value="distinct">Requisições Únicas (Distinct)</MenuItem>
                <MenuItem value="leituras">Total de Leituras (Bruto)</MenuItem>
                <MenuItem value="volume">Volume Total</MenuItem>
                <MenuItem value="volume_formatado">Volume com Unidade</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={carregarDados}
                disabled={loading}
              >
                Aplicar Filtros
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={() => window.print()}
                disabled={dadosProcessados.length === 0}
              >
                Imprimir
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Limpar Filtros
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={executarValidacao}
                disabled={validando}
              >
                {validando ? 'Validando...' : 'Validar Dados'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas resumo */}
      {dadosProcessados.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {dadosProcessados.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Funcionários
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {Object.values(metrica === 'distinct' ? totaisDiarios : totaisDiariosLeituras).reduce((a, b) => a + b, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metrica === 'distinct' ? 'Total Distinct' : 'Total Leituras'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {diasDoMes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dias no Período
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {dadosProcessados.length > 0 ?
                    Math.round(Object.values(metrica === 'distinct' ? totaisDiarios : totaisDiariosLeituras).reduce((a, b) => a + b, 0) / dadosProcessados.length) : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Média por Pessoa ({metrica === 'distinct' ? 'Distinct' : 'Leituras'})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabela Principal */}
      {dadosProcessados.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5', 
                      minWidth: '120px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Setor
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5', 
                      minWidth: '200px',
                      position: 'sticky',
                      left: '120px',
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Nome do Colaborador
                  </TableCell>
                  
                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        minWidth: '60px',
                        fontSize: '0.75rem',
                        padding: '8px 4px'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" display="block">
                          Dia {dia.dia}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {dia.diaSemana}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: '80px' }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dadosSegurosPaginados.map((funcionario, index) => (
                  <TableRow
                    key={funcionario.codigo === 'TOTAL' ? `total-${index}` : funcionario.codigo}
                    sx={{
                      backgroundColor: funcionario.codigo === 'TOTAL' ? '#e8f5e8' : (index % 2 === 0 ? '#fafafa' : 'white'),
                      '&:hover': { backgroundColor: '#f0f0f0' },
                      fontWeight: funcionario.codigo === 'TOTAL' ? 'bold' : 'normal'
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: funcionario.codigo === 'TOTAL' ? 'bold' : 'bold', 
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: funcionario.codigo === 'TOTAL' ? '#e8f5e8' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '120px'
                      }}
                    >
                      <Chip
                        label={funcionario.setor}
                        size="small"
                        color={funcionario.codigo === 'TOTAL' ? 'success' : 'primary'}
                        variant={funcionario.codigo === 'TOTAL' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    
                    <TableCell 
                      sx={{ 
                        fontWeight: funcionario.codigo === 'TOTAL' ? 'bold' : 'bold', 
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: '120px',
                        zIndex: 2,
                        backgroundColor: funcionario.codigo === 'TOTAL' ? '#e8f5e8' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '200px'
                      }}
                    >
                      {funcionario.nome}
                    </TableCell>

                    {diasDoMes.map(dia => {
                      const valor = metrica === 'distinct'
                        ? (funcionario.producaoDiariaDistinct[dia.data] || 0)
                        : metrica === 'leituras'
                        ? (funcionario.producaoDiariaLeituras[dia.data] || 0)
                        : metrica === 'volume'
                        ? (funcionario.producaoDiariaVolume[dia.data] || 0)
                        : (funcionario.producaoDiariaVolumeFormatado[dia.data] || '');
                      return renderCelulaProducao(valor, dia.data);
                    })}

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {metrica === 'distinct' ? funcionario.totalDistinct : metrica === 'leituras' ? funcionario.totalLeituras : metrica === 'volume' ? funcionario.totalVolume : '-'}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de totais */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0' }} colSpan={2}>
                    <Typography variant="h6">TOTAL</Typography>
                  </TableCell>

                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {metrica === 'distinct'
                        ? (totaisDiarios[dia.data] || 0)
                        : metrica === 'leituras'
                        ? (totaisDiariosLeituras[dia.data] || 0)
                        : metrica === 'volume'
                        ? (totaisDiariosVolume[dia.data] || 0)
                        : '-'}
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      border: '1px solid #e0e0e0',
                      fontSize: '1.1rem'
                    }}
                  >
                    {metrica === 'distinct'
                      ? Object.values(totaisDiarios).reduce((a, b) => a + b, 0)
                      : metrica === 'leituras'
                      ? Object.values(totaisDiariosLeituras).reduce((a, b) => a + b, 0)
                      : metrica === 'volume'
                      ? Object.values(totaisDiariosVolume).reduce((a, b) => a + b, 0)
                      : '-'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <TablePagination
            className="no-print"
            component="div"
            count={dadosProcessados.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Legenda de cores */}
      {dadosProcessados.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom>
            Legenda de Cores
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#ffebee', border: '1px solid #ccc' }} />
                <Typography variant="body2">0 (Sem produção)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#fff3e0', border: '1px solid #ccc' }} />
                <Typography variant="body2">1-49 (Baixa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#f3e5f5', border: '1px solid #ccc' }} />
                <Typography variant="body2">50-99 (Média)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e8f5e8', border: '1px solid #ccc' }} />
                <Typography variant="body2">100-149 (Boa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e3f2fd', border: '1px solid #ccc' }} />
                <Typography variant="body2">150+ (Excelente)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, backgroundColor: '#757575', borderRadius: '50%' }} />
                <Typography variant="body2">Final de semana</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Resultado da Validação */}
      {resultadoValidacao && (
        <Paper sx={{ p:2, mt:3 }} className="no-print">
          <Typography variant="h6" gutterBottom>Resultado da Validação</Typography>
          {resultadoValidacao.success && resultadoValidacao.dados ? (
            <>
              <Typography variant="body2" gutterBottom>
                Período: {resultadoValidacao.dados.periodo.inicio} a {resultadoValidacao.dados.periodo.fim}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Funcionários considerados: {resultadoValidacao.dados.funcionarios}
              </Typography>
              <Typography variant="body2" gutterBottom color={resultadoValidacao.dados.ok ? 'green' : 'error'}>
                Status: {resultadoValidacao.dados.ok ? 'OK - Sem divergências' : 'Divergências encontradas'}
              </Typography>
              {resultadoValidacao.dados.divergencias.length > 0 && (
                <Box sx={{ mt:2 }}>
                  <Typography variant="subtitle2">Divergências:</Typography>
                  <Table size="small" sx={{ mt:1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Funcionário</TableCell>
                        <TableCell>Esperado</TableCell>
                        <TableCell>Calculado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultadoValidacao.dados.divergencias.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{d.tipo}</TableCell>
                          <TableCell>{d.codigo || '-'}</TableCell>
                          <TableCell>{d.esperado}</TableCell>
                          <TableCell>{d.calculado}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="error">Erro ao validar</Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioTabular;
